import {
  Box,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  linearProgressClasses,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

import { useEffect, useState } from "react";
import dayjs from "dayjs";
import useStyles from "../styles";
import LineChartComponent from "../../../../../../components/lineComponent";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  BarChartOutlined,
  StackedLineChartOutlined,
  Timeline,
} from "@mui/icons-material";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);

const timeZone = "America/Sao_Paulo";

export default function AtendimentosGraphComponent({
  data = [],
  titleGraph,
  loading = false,
  handleAplicarZoom = () => {},
  isZoomEnabled = false,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [chartData, setChartData] = useState([]);
  const [colors, setColors] = useState({});
  const [isBar, setIsBar] = useState(true);
  const [granularity, setGranularity] = useState("day");

  useEffect(() => {
    if (!data?.length) return;

    const parsed = groupData(data, granularity);

    const colorAbertos = theme?.palette?.colors?.blue?.medium;
    const colorResolvidos = theme?.palette?.colors?.green?.light;

    setChartData(parsed);
    setColors({
      totalAbertos: colorAbertos,
      totalResolvidos: colorResolvidos,
    });
  }, [data, granularity]);

  //caso venha apenas um ponto ele vai duplicar para plotar uma linah
  const fixSinglePoint = (data) => {
    if (data?.length === 1) {
      const clone = { ...data[0], _fake: true };
      return [data[0], clone];
    }
    return data;
  };

  const groupData = (data, granularity = "day") => {
    if (!data?.length) return [];

    //criar uma copia do array e gera a diferenca entre eles
    const sorted = [...data]?.sort(
      (a, b) => new Date(a?.date) - new Date(b?.date),
    );

    //pegaa a primeira e a ultima data para não sair do range slecionado
    const first = dayjs(sorted[0]?.date)?.tz(timeZone);
    const last = dayjs(sorted[sorted?.length - 1]?.date)?.tz(timeZone);

    const grouped = {};

    sorted?.forEach((item) => {
      const date = dayjs(item?.date)?.tz(timeZone);

      let key;

      switch (granularity) {
        //formata inicio e fim do range
        case "week": {
          let startWeek = date?.startOf("week");
          let endWeek = date?.endOf("week");

          if (startWeek?.isBefore(first)) startWeek = first;

          if (endWeek?.isAfter(last)) endWeek = last;

          key = `${startWeek?.format("YYYY-MM-DD")}||${endWeek?.format(
            "YYYY-MM-DD",
          )}`;
          break;
        }

        case "month": {
          const monthStart = dayjs(date)?.startOf("month");
          const monthEnd = dayjs(date)?.endOf("month");

          const start = monthStart?.isBefore(first) ? first : monthStart;
          const end = monthEnd?.isAfter(last) ? last : monthEnd;

          key = `${start?.format("YYYY-MM-DD")}||${end?.format("YYYY-MM-DD")}`;
          break;
        }

        default:
          key = date?.format("YYYY-MM-DD");
      }

      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          totalAbertos: 0,
          totalResolvidos: 0,
        };
      }

      grouped[key].totalAbertos += Number(item?.totalAbertos);
      grouped[key].totalResolvidos += Number(item?.totalResolvidos);
    });

    return Object?.values(grouped);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload?.length) return null;

    const row = payload[0]?.payload;

    return (
      <Box sx={styles?.boxContainerTooltip}>
        <Typography fontSize={"14px"}>
          {granularity === "week" &&
            (() => {
              const [start, end] = row.date.split("||");
              return `Data: ${dayjs(start).format("DD/MM")} - ${dayjs(
                end,
              ).format("DD/MM")}`;
            })()}

          {granularity === "month" &&
            (() => {
              const [start, end] = row.date.split("||");
              return `Data: ${dayjs(start).format("DD/MM")} - ${dayjs(
                end,
              ).format("DD/MM")}`;
            })()}

          {granularity === "day" &&
            `Data: ${dayjs(row?.date).format("DD/MM/YYYY")}`}
        </Typography>

        <Box sx={styles?.containerTooltip}>
          <Box
            sx={{
              ...styles.boxValueTooltip,
              backgroundColor: colors.totalAbertos,
            }}
          />
          <Typography sx={styles.textTooltip}>Abertos: </Typography>
          <Typography>{row.totalAbertos}</Typography>
        </Box>

        <Box sx={styles.containerTooltip}>
          <Box
            sx={{
              ...styles.boxValueTooltip,
              backgroundColor: colors.totalResolvidos,
            }}
          />
          <Typography sx={styles.textTooltip}>Resolvidos: </Typography>
          <Typography>{row.totalResolvidos}</Typography>
        </Box>

        <Box sx={styles.containerTooltip}>
          <Typography sx={styles.textTooltip}>Total: </Typography>
          <Typography>{row.totalAbertos + row.totalResolvidos}</Typography>
        </Box>
      </Box>
    );
  };

  const formatValue = (value) => `${value}`;

  return (
    <Paper
      elevation={theme.palette.mode === "dark" ? 0 : 3}
      sx={{
        ...styles.paper,
        backgroundColor: theme.palette.background.paper,
        width: "100%",
      }}
    >
      {loading && <LinearProgress sx={styles.linearProgressLoading} />}

      <Box sx={styles.containerComponent}>
        <Box sx={styles.boxTopContent}>
          <Typography sx={styles.cardSubText}>{titleGraph}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="granularity-label">Faixa de Tempo</InputLabel>
              <Select
                labelId="granularity-label"
                value={granularity}
                label="Granularidade"
                onChange={(e) => setGranularity(e.target.value)}
              >
                <MenuItem value="day">Dia</MenuItem>
                <MenuItem value="week">Semana</MenuItem>
                <MenuItem value="month">Mês</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              variant="contained"
              onClick={() => setIsBar(!isBar)}
              sx={styles.iconButton}
            >
              {isBar ? <BarChartOutlined /> : <Timeline />}
            </IconButton>
          </Box>
        </Box>
        {/* GRÁFICO */}
        <Box sx={styles.containerGrid}>
          <Grid container spacing={1} columns={{ xs: 1, sm: 1, md: 6, lg: 6 }}>
            <Grid item xs={1} sm={1} md={6} lg={6}>
              <Box width="100%" height={120}>
                {isBar ? (
                  <LineChartComponent
                    data={fixSinglePoint(chartData)}
                    isZoomEnabled={isZoomEnabled}
                    onZoomChange={(range) => {
                      const startDate = dayjs(range.start).tz(timeZone);
                      const endDate = dayjs(range.end).tz(timeZone);

                      handleAplicarZoom(startDate, endDate);
                    }}
                    maxTicks={10}
                    xOptions={{
                      dataKey: "date",
                      tickFormatter: (value) => {
                        if (granularity === "day") {
                          return dayjs(value).format("DD/MM");
                        }

                        if (granularity === "week") {
                          const [start, end] = value.split("||");
                          return `${dayjs(start).format("DD/MM")}-${dayjs(
                            end,
                          ).format("DD/MM")}`;
                        }

                        if (granularity === "month") {
                          const [start, end] = value.split("||");
                          return `${dayjs(start).format("DD/MM")}-${dayjs(
                            end,
                          ).format("DD/MM")}`;
                        }

                        return value;
                      },
                    }}
                    yOptions={{
                      tickFormatter: formatValue,
                    }}
                    customTooltip={CustomTooltip}
                    objectColorsLine={{
                      totalAbertos: colors.totalAbertos,
                      totalResolvidos: colors.totalResolvidos,
                      total: theme.palette.colors.blue.medium,
                    }}
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 10, right: 0, left: -15, bottom: 0 }}
                    >
                      <CartesianGrid
                        vertical={false}
                        horizontal={true}
                        stroke={theme.palette.divider}
                        strokeDasharray="3"
                      />

                      <XAxis
                        dataKey="date"
                        tickFormatter={(key) => {
                          if (granularity === "day") {
                            return dayjs(key).tz(timeZone).format("DD/MM");
                          }

                          if (granularity === "week") {
                            const [start, end] = key.split("||");
                            return `${dayjs(start).format("DD/MM")} - ${dayjs(
                              end,
                            ).format("DD/MM")}`;
                          }

                          if (granularity === "month") {
                            const [start, end] = key.split("||");
                            return `${dayjs(start).format("DD/MM")} - ${dayjs(
                              end,
                            ).format("DD/MM")}`;
                          }

                          return key;
                        }}
                        tick={{
                          fill: theme.palette.text.primary,
                          fontSize: 10,
                        }}
                      />

                      <YAxis
                        tickFormatter={formatValue}
                        tick={{
                          fill: theme.palette.text.primary,
                          fontSize: 10,
                        }}
                      />

                      <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: `${theme.palette.white.main}1a` }}
                      />

                      <Bar
                        dataKey="totalAbertos"
                        name="Abertos"
                        barSize={15}
                        radius={[5, 5, 0, 0]}
                      >
                        {fixSinglePoint(chartData).map((entry, index) => (
                          <Cell
                            key={`abertos-${index}`}
                            fill={colors.totalAbertos}
                          />
                        ))}
                      </Bar>

                      <Bar
                        dataKey="totalResolvidos"
                        name="Resolvidos"
                        barSize={15}
                        radius={[5, 5, 0, 0]}
                      >
                        {fixSinglePoint(chartData).map((entry, index) => (
                          <Cell
                            key={`resolvidos-${index}`}
                            fill={colors.totalResolvidos}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
}
